import React, { useState } from 'react';
import { Download, Share, Plus, X, Smartphone, Monitor } from 'lucide-react';
import usePWAInstall from '../hooks/usePWAInstall';

/**
 * Renders the "Télécharger l'app" CTA.
 * - Hidden only when the app is already installed (display-mode: standalone).
 * - Chromium browsers: triggers the native beforeinstallprompt.
 * - iOS Safari: opens a modal with the "Ajouter à l'écran d'accueil" steps.
 * - Other browsers: opens a generic modal with the browser menu path.
 */
export default function InstallAppButton({ className = 'site-btn site-btn-outline', label = "Télécharger l'app", compact = false }) {
  const { canInstall, isIOS, isInstalled, install } = usePWAInstall();
  const [modal, setModal] = useState(null); // null | 'ios' | 'generic'

  if (isInstalled) return null;

  const handleClick = async () => {
    if (canInstall) {
      const outcome = await install();
      if (outcome === 'unsupported') setModal('generic');
      return;
    }
    setModal(isIOS ? 'ios' : 'generic');
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={className}
        data-testid="install-app-btn"
        title="Installer l'application sur votre appareil"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      >
        <Download size={16} />
        {!compact && <span>{label}</span>}
      </button>

      {modal && (
        <div
          className="install-modal-backdrop"
          onClick={() => setModal(null)}
          data-testid="install-modal"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,90,92,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, padding: 16,
          }}
        >
          <div
            className="install-modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 16, maxWidth: 460, width: '100%',
              padding: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              position: 'relative',
            }}
          >
            <button
              onClick={() => setModal(null)}
              aria-label="Fermer"
              style={{
                position: 'absolute', top: 10, right: 10, background: 'transparent',
                border: 'none', cursor: 'pointer', padding: 6,
              }}
              data-testid="install-modal-close"
            >
              <X size={20} />
            </button>

            {modal === 'ios' ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <Smartphone size={26} color="#4A90A4" />
                  <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Installer sur iPhone / iPad</h3>
                </div>
                <p style={{ color: '#475569', marginBottom: 14 }}>
                  Sur iOS, l&apos;installation se fait via Safari&nbsp;:
                </p>
                <ol style={{ paddingLeft: '1.2rem', color: '#0f172a', lineHeight: 1.7 }}>
                  <li style={{ marginBottom: 6 }}>
                    Appuyez sur l&apos;icône <Share size={16} style={{ verticalAlign: 'middle', margin: '0 4px' }} />
                    <strong>Partager</strong> en bas de Safari.
                  </li>
                  <li style={{ marginBottom: 6 }}>
                    Faites défiler et choisissez <strong>« Sur l&apos;écran d&apos;accueil »</strong>
                    <Plus size={16} style={{ verticalAlign: 'middle', margin: '0 4px' }} />.
                  </li>
                  <li>Confirmez avec <strong>« Ajouter »</strong>. L&apos;app apparaîtra comme une icône à part entière.</li>
                </ol>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <Monitor size={26} color="#4A90A4" />
                  <h3 style={{ margin: 0, fontSize: '1.15rem' }}>Installer l&apos;application</h3>
                </div>
                <p style={{ color: '#475569', marginBottom: 14 }}>
                  Pour installer le CMT sur votre appareil&nbsp;:
                </p>
                <ul style={{ paddingLeft: '1.2rem', color: '#0f172a', lineHeight: 1.7, listStyle: 'disc' }}>
                  <li><strong>Android</strong> — Chrome : menu <strong>⋮</strong> → <em>« Installer l&apos;application »</em> ou <em>« Ajouter à l&apos;écran d&apos;accueil »</em>.</li>
                  <li><strong>Ordinateur</strong> — Chrome / Edge : icône <Download size={14} style={{ verticalAlign: 'middle' }} /> dans la barre d&apos;adresse, à droite de l&apos;URL.</li>
                  <li><strong>Firefox / autres</strong> — l&apos;installation n&apos;est pas prise en charge. Ajoutez plutôt le site à vos favoris.</li>
                </ul>
                <p style={{ marginTop: 12, color: '#64748b', fontSize: 13 }}>
                  Astuce&nbsp;: rechargez la page si le bouton d&apos;installation du navigateur n&apos;apparaît pas immédiatement.
                </p>
              </>
            )}
            <button
              onClick={() => setModal(null)}
              className="site-btn site-btn-primary"
              style={{ marginTop: 16, width: '100%' }}
              data-testid="install-modal-ok"
            >
              J&apos;ai compris
            </button>
          </div>
        </div>
      )}
    </>
  );
}
